import { useState, useMemo, useEffect, useId } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'ui/modal';
import { Link } from 'ui/links';
import { useSelector, useDispatch } from 'react-redux';
import { FlexSpacer } from 'ui/flexSpacer';
import { selectIsProductRecommendationsFeatureFlagOn } from 'core/userContext/selectors';
import { Tooltip } from 'ui/tooltip';
import { MakeCopy16 } from 'ui/icons/aurora';
import { PrimaryButton } from 'ui/button';
import { SuccessBanner } from 'ui/banner';
import { copyToClipboard } from 'utils/clipboard';
import {
  CATALOG_COLLECTION_HANDLEBARS,
  CATALOG_RECOMMENDATION_HANDLEBARS,
  COLLECTIONS_HANDLEBARS_HELP_DOC_URL,
} from 'modules/catalog/catalogConstants';
import { closeHandlebarsModal, copyHandlebars } from 'modules/catalog/collections/actions';
import {
  selectIsHandlebarsModalOpen,
  selectSelectedCollectionName,
  selectUsesSmartRecosHandlebar,
} from 'modules/catalog/collections/selectors';
import { StyledTextArea } from './CollectionsHandlebarsModal.styled';

export const CollectionsHandlebarsModal = () => {
  const modalId = useId();
  const dispatch = useDispatch();
  const isHandlebarsModalOpen = useSelector(selectIsHandlebarsModalOpen);
  const selectedCollectionName = useSelector(selectSelectedCollectionName);
  const isProductRecommendationsOn = useSelector(selectIsProductRecommendationsFeatureFlagOn);
  const usesSmartRecosHandlebar = useSelector(selectUsesSmartRecosHandlebar);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handlebar = useMemo(() => {
    const shouldUseProductRecosHandlebar = isProductRecommendationsOn && usesSmartRecosHandlebar;
    const type = shouldUseProductRecosHandlebar
      ? CATALOG_RECOMMENDATION_HANDLEBARS
      : CATALOG_COLLECTION_HANDLEBARS;

    return `{{#${type} "${selectedCollectionName}" as | collection |}}\n<!-- Your html goes here -->\n{{/${type}}}`;
  }, [selectedCollectionName, isProductRecommendationsOn, usesSmartRecosHandlebar]);

  const handleCopy = () => {
    dispatch(copyHandlebars());
    copyToClipboard(handlebar);
    setShowSuccessMessage(true);
  };

  const onClose = () => {
    dispatch(closeHandlebarsModal());
  };

  useEffect(() => {
    if (showSuccessMessage) {
      setTimeout(() => setShowSuccessMessage(false), 5000);
    }
  }, [showSuccessMessage]);

  return isHandlebarsModalOpen ? (
    <Modal id={modalId} isModalOpen={isHandlebarsModalOpen} onClose={onClose}>
      <ModalHeader>Catalog Collection Handlebars</ModalHeader>
      <ModalBody>
        <FlexSpacer flexDirection="column" alignItems="stretch">
          {showSuccessMessage && <SuccessBanner>Copied to clipboard</SuccessBanner>}
          <Tooltip isEnabled={!showSuccessMessage} tooltipContent="Click to copy">
            {/* this should not be a text area. we're over-extending it with an unexpected "onclick". thus the expect error below */}
            {/* @ts-expect-error */}
            <StyledTextArea rows={3} onClick={handleCopy} readOnly value={handlebar} />
          </Tooltip>
          <Link target="_blank" hasOpenNewTabIcon href={COLLECTIONS_HANDLEBARS_HELP_DOC_URL}>
            Using catalog collections in templates
          </Link>
        </FlexSpacer>
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <PrimaryButton onClick={handleCopy}>
          <MakeCopy16 />
          Copy
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  ) : null;
};
